/**
 * Unit dates derived from the plan and the calendar, never stored (KTD5). The worker and the
 * browser both call these, so a date can never drift from the week counts. Pure.
 */
import { addDays, breakIn, mondayOf, schoolDays, teachingWeeks, weekOf } from './calendar';
import type { Calendar, IsoDate, Plan, PlanUnit } from './types';

export interface ScheduledUnit extends PlanUnit {
  /** The Monday of each week the unit covers, in order. Empty for a done unit with zero weeks. */
  weekStarts: IsoDate[];
  /** First and last school day the unit covers; null when it has no weeks. */
  start: IsoDate | null;
  end: IsoDate | null;
  /** Any of its weeks fall after the last day (R11). */
  pastLastDay: boolean;
  weeksOver: number;
}

export interface Overrun {
  /** Total week counts less teaching weeks, floored at zero (R15). */
  weeksOver: number;
  units: { id: string; name: string; weeksOver: number }[];
}

export interface WeekView {
  weekStart: IsoDate;
  units: ScheduledUnit[];
}

/** What the weekly view shows (R13). Weekends belong to the coming week. */
export type WeeklyView =
  | { state: 'before_first_day'; firstDay: IsoDate; nextWeek: WeekView | null }
  | { state: 'teaching'; thisWeek: WeekView; nextWeek: WeekView | null }
  | { state: 'break'; breakName: string; nextWeek: WeekView | null }
  | { state: 'finished'; lastDay: IsoDate };

type Units = Pick<Plan, 'units'>;

const weekCount = (weeks: number) => (Number.isFinite(weeks) ? Math.max(0, Math.floor(weeks)) : 0);

/** Assigns consecutive teaching weeks to units in order; weeks past the last day run on weekly. */
export function schedule(plan: Units, calendar: Calendar): ScheduledUnit[] {
  const weeks = teachingWeeks(calendar);
  const firstOverflow = addDays(mondayOf(calendar.lastDay), 7);
  const weekAt = (i: number) => (i < weeks.length ? weeks[i]! : addDays(firstOverflow, 7 * (i - weeks.length)));
  const firstDayOf = (monday: IsoDate, i: number) => (i < weeks.length ? schoolDays(calendar, monday)[0]! : monday);
  const lastDayOf = (monday: IsoDate, i: number) => (i < weeks.length ? schoolDays(calendar, monday).at(-1)! : addDays(monday, 4));

  let next = 0;
  return plan.units.map((unit) => {
    const from = next;
    next += weekCount(unit.weeks);
    const indexes = Array.from({ length: next - from }, (_, k) => from + k);
    const weekStarts = indexes.map(weekAt);
    const weeksOver = indexes.filter(i => i >= weeks.length).length;
    return {
      ...unit,
      weekStarts,
      start: weekStarts.length ? firstDayOf(weekStarts[0]!, from) : null,
      end: weekStarts.length ? lastDayOf(weekStarts.at(-1)!, next - 1) : null,
      pastLastDay: weeksOver > 0,
      weeksOver,
    };
  });
}

/** The units past the last day and the weeks over. Computed, never applied (KTD7). */
export function overrun(plan: Units, calendar: Calendar): Overrun {
  const over = schedule(plan, calendar).filter(u => u.pastLastDay);
  return {
    weeksOver: over.reduce((sum, u) => sum + u.weeksOver, 0),
    units: over.map(({ id, name, weeksOver }) => ({ id, name, weeksOver })),
  };
}

/**
 * The units this week and the next teaching week, as of the teacher's `today` (KTD8). Pass
 * `scheduled` when the caller already has `schedule(plan, calendar)`.
 */
export function weekly(plan: Units, calendar: Calendar, today: IsoDate, scheduled?: ScheduledUnit[]): WeeklyView {
  if (today > calendar.lastDay) return { state: 'finished', lastDay: calendar.lastDay };
  const rows = scheduled ?? schedule(plan, calendar);
  const weeks = teachingWeeks(calendar);
  const view = (weekStart: IsoDate | undefined): WeekView | null =>
    weekStart === undefined ? null : { weekStart, units: rows.filter(u => u.weekStarts.includes(weekStart)) };

  if (today < calendar.firstDay) return { state: 'before_first_day', firstDay: calendar.firstDay, nextWeek: view(weeks[0]) };
  const week = weekOf(today);
  const nextWeek = view(weeks.find(w => w > week));
  if (weeks.includes(week)) return { state: 'teaching', thisWeek: view(week)!, nextWeek };
  return { state: 'break', breakName: breakIn(calendar, week)?.name ?? 'No school', nextWeek };
}
